import { useState, useMemo, useEffect } from 'react';
import { FileText, Calendar, ChevronRight, ArrowLeft } from 'lucide-react';
import { useGetPublicArticlesQuery, useGetModulesQuery, Article } from '../../store/apis/helpCenter.api';
import { ArticleListSkeleton } from './Skeleton';
import { Button, Badge, SearchInput } from '../common';
import { scrollToTop } from '../survey/hooks';
import { markdownToPlainText } from '../../utils/helpCenterMarkdown';

interface ArticleListProps {
  categoryId: string;
  onBack: () => void;
  onArticleClick: (articleId: string) => void;
}

export function ArticleList({ categoryId, onBack, onArticleClick }: ArticleListProps) {
  useEffect(() => {
    scrollToTop();
  }, [categoryId]);

  const { data: publicArticlesData, isLoading: isArticlesLoading } = useGetPublicArticlesQuery();
  const { data: allModules = [], isLoading: isModulesLoading } = useGetModulesQuery();
  const [searchQuery, setSearchQuery] = useState('');

  const handleClick = (id: string) => {
    if (onArticleClick) {
      onArticleClick(id);
    }
  };

  const articles = publicArticlesData?.[categoryId] || [];
  const isLoading = isArticlesLoading || isModulesLoading;

  const filteredArticles = useMemo(() => {
    if (!searchQuery) return articles;
    const q = searchQuery.toLowerCase();
    return articles.filter(a =>
      a.title.toLowerCase().includes(q) ||
      a.content.toLowerCase().includes(q)
    );
  }, [articles, searchQuery]);

  // Dynamically resolve category name from the module list
  const moduleInfo = allModules.find(m => m.code === categoryId);
  const categoryName = moduleInfo?.name || categoryId.split('_').map(word => word.charAt(0) + word.slice(1).toLowerCase()).join(' ');
  const categoryDescription = `Documentation and guides for the ${categoryName.toLowerCase()} module.`;

  return (
    <div className="bg-surface-muted p-4 sm:p-6 lg:p-8 min-h-screen">
      {/* Category Header */}
      <div className="mb-10">
        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6">
          <div className="space-y-3 flex-1">
            <div className="flex items-center gap-2 text-primary font-bold text-[10px] uppercase tracking-widest">
              Category
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 mb-2">{categoryName}</h1>
              <p className="text-slate-500 max-w-2xl">{categoryDescription}</p>
            </div>
          </div>

          <div className="flex flex-col items-end gap-4 w-full lg:w-auto">
            <div className="flex items-center gap-4">
              {!isLoading && (
                <Badge variant="gray" size="middle" outline>
                  {filteredArticles?.length || 0} Articles
                </Badge>
              )}
              <Button
                appearance="ghost"
                onClick={onBack}
                size="small"
                className="text-slate-500"
                icon={<ArrowLeft size={16} />}
              >
                Back
              </Button>
            </div>
            <div className="w-full md:w-96">
              <SearchInput
                placeholder="Search in category..."
                value={searchQuery}
                onChange={(val) => setSearchQuery(val)}
                className="w-full"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Articles List Container */}
      {isLoading ? (
        <ArticleListSkeleton />
      ) : (
        <div className="space-y-4">
          {filteredArticles && filteredArticles.length > 0 ? (
            filteredArticles.map((article: Article) => (
              <button
                key={article._id}
                onClick={() => handleClick(article._id)}
                className="w-full group p-6 bg-white rounded-xl border border-slate-200 shadow-soft hover:shadow-md hover:border-primary/20 transition-all text-left flex items-center gap-6"
              >
                {/* Article Image */}
                {article.imageUrl && (
                  <div className="hidden md:block flex-shrink-0">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      className="w-20 h-14 object-cover rounded-lg border border-slate-100"
                    />
                  </div>
                )}

                {/* Article Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <Badge variant="blue" size="small">
                      Article
                    </Badge>
                    <div className="flex items-center gap-1.5 text-[10px] text-slate-400">
                      <Calendar size={10} />
                      <span>
                        {article.updatedAt
                          ? (() => {
                            const date = new Date(article.updatedAt);
                            const d = String(date.getDate()).padStart(2, '0');
                            const m = String(date.getMonth() + 1).padStart(2, '0');
                            const y = date.getFullYear();
                            return `${d}/${m}/${y}`;
                          })()
                          : 'Recently'}
                      </span>
                    </div>
                  </div>

                  <h3 className="text-base font-bold text-slate-900 mb-1 group-hover:text-primary transition-colors flex items-center gap-2">
                    {article.title}
                    <ChevronRight size={14} className="opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                  </h3>

                  <p className="text-xs text-slate-500 truncate leading-relaxed">
                    {markdownToPlainText(article.content, 150)}
                  </p>
                </div>
              </button>
            ))
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 shadow-soft text-center py-20">
              <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-3 border border-slate-100">
                <FileText size={20} className="text-slate-300" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-1">No articles yet</h3>
              <p className="text-slate-500 max-w-xs mx-auto text-xs">
                We're currently working on documentation for this module.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
